"use client";

import React from "react";
import { Building2, GraduationCap, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ComparisonRow {
  criterion: string;
  center: string;
  school: string;
}
const comparisonData: ComparisonRow[] = [{
  criterion: "Mô hình triển khai",
  center: "Hợp tác nhượng quyền theo cơ sở",
  school: "Triển khai chương trình theo tiết học/khối lớp"
}, {
  criterion: "Chi phí ban đầu",
  center: "50.000.000 VNĐ cơ sở đầu tiên, 20.000.000 VNĐ từ cơ sở thứ 2",
  school: "0 VNĐ cho dạy chính khóa/câu lạc bộ, 20.000.000 VNĐ/khối cho chuyển giao chương trình, 50-100 triệu VNĐ cho đối tác liên kết"
}, {
  criterion: "Cách tính phí",
  center: "Phí hợp tác cố định theo tháng/cơ sở",
  school: "Theo tiết/buổi/học sinh, hoặc % doanh thu cho đối tác liên kết"
}, {
  criterion: "Hình thức đào tạo",
  center: "Robotics và Lập trình/AI",
  school: "Chính khóa, câu lạc bộ ngoài giờ, hoặc chuyển giao chương trình"
}, {
  criterion: "Quy mô triển khai",
  center: "Theo từng cơ sở",
  school: "Theo khối lớp/trường"
}];
export default function PartnershipModelComparison() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="partnershipmodelcomparison" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-serif tracking-tight text-foreground">
            CHỌN MÔ HÌNH HỢP TÁC PHÙ HỢP
          </h2>
        </div>

        {/* Desktop & Tablet Table View */}
        <div className="hidden md:block rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/60">
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="w-1/4 py-5 px-6 font-semibold text-foreground text-base">
                  Tiêu chí
                </TableHead>
                <TableHead className="w-[37.5%] py-5 px-6 text-base">
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <Building2 className="size-5 shrink-0" />
                    <span>TRUNG TÂM</span>
                  </div>
                </TableHead>
                <TableHead className="w-[37.5%] py-5 px-6 text-base">
                  <div className="flex items-center gap-2 text-accent-foreground dark:text-foreground font-bold">
                    <GraduationCap className="size-5 shrink-0 text-accent" />
                    <span className="text-foreground">NHÀ TRƯỜNG</span>
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((row, index) => <TableRow key={index} data-index={index} className="border-b border-border/70 last:border-0 hover:bg-muted/30 transition-colors">
                  <TableCell className="py-5 px-6 font-medium text-foreground align-top">
                    {row.criterion}
                  </TableCell>
                  <TableCell className="py-5 px-6 text-muted-foreground align-top leading-relaxed">
                    {row.center}
                  </TableCell>
                  <TableCell className="py-5 px-6 text-muted-foreground align-top leading-relaxed">
                    {row.school}
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        {/* Mobile Side-by-Side Stack Cards */}
        <div className="grid grid-cols-1 gap-6 md:hidden">
          {/* Trung Tâm Card */}
          <Card className="border border-border bg-card shadow-sm">
            <CardHeader className="border-b border-border pb-4 bg-muted/40">
              <div className="flex items-center gap-2">
                <Building2 className="size-5 text-primary shrink-0" />
                <CardTitle className="text-xl font-bold text-primary">
                  TRUNG TÂM
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {comparisonData.map((row, index) => <div key={index} data-index={index} className="space-y-1 pb-3 border-b border-border/50 last:border-0 last:pb-0">
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block">
                    {row.criterion}
                  </span>
                  <p className="text-sm font-medium text-foreground leading-snug">
                    {row.center}
                  </p>
                </div>)}
            </CardContent>
          </Card>

          {/* Nhà Trường Card */}
          <Card className="border border-border bg-card shadow-sm">
            <CardHeader className="border-b border-border pb-4 bg-muted/40">
              <div className="flex items-center gap-2">
                <GraduationCap className="size-5 text-accent shrink-0" />
                <CardTitle className="text-xl font-bold text-foreground">
                  NHÀ TRƯỜNG
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {comparisonData.map((row, index) => <div key={index} data-index={index} className="space-y-1 pb-3 border-b border-border/50 last:border-0 last:pb-0">
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block">
                    {row.criterion}
                  </span>
                  <p className="text-sm font-medium text-foreground leading-snug">
                    {row.school}
                  </p>
                </div>)}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}